<!--
  @component AccountTokensPanel — API token CRUD panel.
  tier:      panel (mounts inside AccountScreen)
  reads:     useTokensIO          → token CRUD + audit
             useIdentityIO        → user's projects (for project-scoping picker)
  effects:   tokens.list / .create / .revoke / .rotate

  REVEAL-ONCE UX
  The `rendered` value is shown ONCE per create/rotate and never
  persisted. The user copies it to their env file / password manager
  / CI secret store. After dismiss, the panel only ever sees
  prefixVisible + the hash on the server.
-->
<script lang="ts">
  import { onMount } from "svelte";
  import { useTokensIO, type TokensRowPublic, type TokensRendered } from "../../../global/io/nodes/deployment/TokensIO.svelte";
  import { useIdentityIO } from "./IdentityIO.svelte";
  import GuardedActionModal from "../../../global/visuals/primitives/GuardedActionModal.svelte";

  const tokens = useTokensIO();
  const identity = useIdentityIO();

  // ── List state ─────────────────────────────────────────────
  let rows = $state<readonly TokensRowPublic[]>([]);
  let listing = $state(false);
  let listError = $state<string | null>(null);

  async function refresh(): Promise<void> {
    listing = true;
    listError = null;
    try {
      const r = await tokens.list();
      rows = r.tokens;
    } catch (e) {
      listError = e instanceof Error ? e.message : String(e);
    } finally {
      listing = false;
    }
  }

  onMount(() => { void refresh(); });

  // ── Create form ────────────────────────────────────────────
  let label = $state("");
  let preset = $state<"read" | "deploy" | "admin">("deploy");
  let env = $state<"test" | "live">("live");
  let category = $state<TokensRowPublic["category"]>("personal");
  let projectScopeMode = $state<"all" | "restricted">("all");
  let selectedProjectIds = $state<readonly string[]>([]);
  let ttlOption = $state<"never" | "30d" | "90d" | "365d">("never");
  let creating = $state(false);
  let createError = $state<string | null>(null);
  let revealed = $state<TokensRendered | null>(null);
  let copiedFlash = $state(false);

  function resetForm(): void {
    label = "";
    preset = "deploy";
    env = "live";
    category = "personal";
    projectScopeMode = "all";
    selectedProjectIds = [];
    ttlOption = "never";
    createError = null;
  }

  function ttlToMs(opt: string): number | null {
    const day = 24 * 60 * 60 * 1000;
    if (opt === "30d") return 30 * day;
    if (opt === "90d") return 90 * day;
    if (opt === "365d") return 365 * day;
    return null;
  }

  async function createToken(): Promise<void> {
    if (!label.trim()) return;
    creating = true;
    createError = null;
    try {
      const ttlMs = ttlToMs(ttlOption);
      const projectScopeIds =
        projectScopeMode === "restricted" && selectedProjectIds.length > 0
          ? selectedProjectIds
          : null;
      revealed = await tokens.create({
        label: label.trim(),
        preset,
        env,
        category,
        projectScopeIds,
        ttlMs,
      });
      resetForm();
      await refresh();
    } catch (e) {
      createError = e instanceof Error ? e.message : String(e);
    } finally {
      creating = false;
    }
  }

  async function copyRendered(): Promise<void> {
    if (!revealed) return;
    try {
      await navigator.clipboard.writeText(revealed.rendered);
      copiedFlash = true;
      setTimeout(() => { copiedFlash = false; }, 1500);
    } catch {
      // Clipboard refused — operator can manual-select.
    }
  }

  // ── Revoke / rotate ────────────────────────────────────────
  let revoking = $state<TokensRowPublic | null>(null);
  let revokeReason = $state("");
  let actionError = $state<string | null>(null);

  async function confirmRevoke(): Promise<void> {
    if (!revoking) return;
    actionError = null;
    try {
      await tokens.revoke(revoking.tokenId, revokeReason.trim() || undefined);
      revoking = null;
      revokeReason = "";
      await refresh();
    } catch (e) {
      actionError = e instanceof Error ? e.message : String(e);
    }
  }

  let rotating = $state<TokensRowPublic | null>(null);
  async function confirmRotate(): Promise<void> {
    if (!rotating) return;
    actionError = null;
    try {
      const r = await tokens.rotate(rotating.tokenId);
      // The rotate result is shaped exactly like a create reveal —
      // surface it through the same panel for consistency.
      revealed = r as TokensRendered;
      rotating = null;
      await refresh();
    } catch (e) {
      actionError = e instanceof Error ? e.message : String(e);
    }
  }

  // ── Display helpers ────────────────────────────────────────
  function timeAgo(ms: number | null): string {
    if (ms == null) return "—";
    const diff = Date.now() - ms;
    if (diff < 60_000) return "just now";
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
    return `${Math.floor(diff / 86_400_000)}d ago`;
  }
  function statusFor(r: TokensRowPublic): "active" | "expired" | "revoked" {
    if (r.revokedAtMs !== null) return "revoked";
    if (r.expiresAtMs !== null && r.expiresAtMs <= Date.now()) return "expired";
    return "active";
  }

  function toggleProject(id: string): void {
    if (selectedProjectIds.includes(id)) {
      selectedProjectIds = selectedProjectIds.filter((p) => p !== id);
    } else {
      selectedProjectIds = [...selectedProjectIds, id];
    }
  }
</script>

<section class="rail">
  <header>
    <h2>API tokens</h2>
    <button type="button" class="link-muted" onclick={() => void refresh()} disabled={listing}>
      {listing ? "refreshing…" : "refresh"}
    </button>
  </header>
  <p class="rail-help">
    Programmatic access via <code>?token=lqd_…</code> on the WebSocket
    handshake. Bearer tokens scope-check every dispatch against the
    granted scopes. Shown ONCE on create — copy then to your env file
    or password manager.
  </p>

  <!-- ── Mint form ───────────────────────────────────────── -->
  <div class="mint-form">
    <label class="row">
      <span>Label</span>
      <input type="text" placeholder="e.g. ci-prod" bind:value={label} spellcheck="false" />
    </label>
    <div class="row split-3">
      <label>
        <span>Preset</span>
        <select bind:value={preset}>
          <option value="read">Read — list-only</option>
          <option value="deploy">Deploy — full release flow</option>
          <option value="admin">Admin — full account (*)</option>
        </select>
      </label>
      <label>
        <span>Env</span>
        <select bind:value={env}>
          <option value="live">live</option>
          <option value="test">test</option>
        </select>
      </label>
      <label>
        <span>Category</span>
        <select bind:value={category}>
          <option value="personal">personal</option>
          <option value="cli">cli</option>
          <option value="ci">ci</option>
          <option value="integration">integration</option>
          <option value="service">service</option>
        </select>
      </label>
    </div>
    <div class="row split-2">
      <label>
        <span>Project scope</span>
        <select bind:value={projectScopeMode}>
          <option value="all">All my projects</option>
          <option value="restricted">Restrict to selected…</option>
        </select>
      </label>
      <label>
        <span>Expires</span>
        <select bind:value={ttlOption}>
          <option value="never">Never</option>
          <option value="30d">30 days</option>
          <option value="90d">90 days</option>
          <option value="365d">365 days</option>
        </select>
      </label>
    </div>
    {#if projectScopeMode === "restricted"}
      <div class="project-chips">
        {#if identity.projects.length === 0}
          <span class="empty">No projects yet — create one in /projects first.</span>
        {/if}
        {#each identity.projects as p (p.id)}
          <button
            type="button"
            class="chip"
            class:on={selectedProjectIds.includes(p.id)}
            onclick={() => toggleProject(p.id)}
          >
            {p.name ?? p.id}
          </button>
        {/each}
      </div>
    {/if}

    <div class="actions">
      <button
        type="button"
        class="primary"
        onclick={() => void createToken()}
        disabled={!label.trim() || creating || (projectScopeMode === "restricted" && selectedProjectIds.length === 0)}
      >
        {creating ? "creating…" : "+ Create token"}
      </button>
      {#if createError}
        <span class="error">{createError}</span>
      {/if}
    </div>
  </div>

  <!-- ── Reveal-once card ────────────────────────────────── -->
  {#if revealed}
    <div class="reveal-card" role="alert">
      <div class="reveal-header">
        <span class="reveal-badge">SHOWN ONCE</span>
        <span class="reveal-name">{revealed.label}</span>
        <span class="reveal-meta">
          {revealed.env} · {revealed.scopes.length} scope{revealed.scopes.length === 1 ? "" : "s"}
        </span>
      </div>
      <code class="reveal-token">{revealed.rendered}</code>
      <div class="reveal-actions">
        <button type="button" onclick={() => void copyRendered()}>
          {copiedFlash ? "copied ✓" : "copy"}
        </button>
        <button type="button" class="link-muted" onclick={() => (revealed = null)}>dismiss</button>
        <span class="reveal-hint">
          Paste into your env file or password manager — this is the
          only time it will be shown.
        </span>
      </div>
    </div>
  {/if}

  <!-- ── List ────────────────────────────────────────────── -->
  {#if listError}
    <p class="error">{listError}</p>
  {/if}
  {#if rows.length === 0 && !listing}
    <p class="caveat">no tokens · use the form above to mint one</p>
  {:else}
    <ul class="token-list">
      {#each rows as r (r.tokenId)}
        {@const status = statusFor(r)}
        <li class="token-row" class:revoked={status !== "active"}>
          <div class="token-main">
            <div class="token-prefix"><code>{r.prefixVisible}…</code></div>
            <div class="token-label">{r.label}</div>
            <div class="token-tags">
              <span class="tag">{r.env}</span>
              <span class="tag">{r.category}</span>
              {#if r.projectScopeIds}
                <span class="tag scope">{r.projectScopeIds.length} project{r.projectScopeIds.length === 1 ? "" : "s"}</span>
              {/if}
              <span class="tag scope">{r.scopes.length === 0 ? "no scopes" : r.scopes.length === 1 && r.scopes[0] === "*" ? "*" : `${r.scopes.length} scope${r.scopes.length === 1 ? "" : "s"}`}</span>
            </div>
            <div class="token-meta">
              created {timeAgo(r.createdAtMs)}
              · last used {timeAgo(r.lastUsedAtMs)}
              {#if r.expiresAtMs}
                · expires {timeAgo(r.expiresAtMs)}
              {/if}
              {#if r.revokedAtMs}
                · revoked {timeAgo(r.revokedAtMs)} ({r.revokedReason})
              {/if}
            </div>
          </div>
          <div class="token-actions">
            <span class="status status-{status}">{status}</span>
            {#if status === "active"}
              <button type="button" class="link" onclick={() => (rotating = r)}>rotate</button>
              <button type="button" class="link-muted destructive" onclick={() => (revoking = r)}>revoke</button>
            {/if}
          </div>
        </li>
      {/each}
    </ul>
  {/if}
  {#if actionError}
    <p class="error">{actionError}</p>
  {/if}
</section>

<GuardedActionModal
  open={revoking !== null}
  title="Revoke API token"
  body={revoking
    ? `Revoke "${revoking.label}" (${revoking.prefixVisible}…). Any process using this token will start receiving SCOPE_DENIED immediately. Cannot be undone.`
    : ""}
  confirmLabel="revoke"
  destructive
  onConfirm={() => void confirmRevoke()}
  onCancel={() => { revoking = null; revokeReason = ""; }}
/>

<GuardedActionModal
  open={rotating !== null}
  title="Rotate API token"
  body={rotating
    ? `Mint a fresh token with the same scopes + project restrictions as "${rotating.label}", then revoke the current one. The new token is shown ONCE — paste it into your env file BEFORE dismissing the reveal.`
    : ""}
  confirmLabel="rotate"
  onConfirm={() => void confirmRotate()}
  onCancel={() => (rotating = null)}
/>

<style>
  .rail {
    padding: var(--space-4);
    background: var(--glass-fill);
    border: 0.5px solid var(--border);
    border-radius: var(--radius-md);
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }
  .rail header { display: flex; justify-content: space-between; align-items: baseline; }
  h2 {
    margin: 0; font-size: 12px; font-weight: 600;
    color: var(--text-tertiary);
    text-transform: uppercase; letter-spacing: 0.08em;
  }
  .rail-help { margin: 0; font-size: 12px; color: var(--text-secondary); line-height: 1.55; }
  .rail-help code { font-family: var(--type-mono); font-size: 11px; color: var(--accent-text); }
  .caveat { margin: 0; font-size: 11px; color: var(--text-tertiary); }
  .error { margin: 0; font-size: 12px; color: var(--error, #d05); }

  .mint-form { display: flex; flex-direction: column; gap: var(--space-2); }
  .mint-form .row { display: flex; flex-direction: column; gap: 4px; }
  .mint-form .row.split-2 { display: grid; grid-template-columns: 1fr 1fr; gap: var(--space-2); }
  .mint-form .row.split-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: var(--space-2); }
  .mint-form label > span {
    font-size: 10px; text-transform: uppercase; letter-spacing: 0.08em;
    color: var(--text-tertiary);
  }
  .mint-form input, .mint-form select {
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-sm);
    border: 0.5px solid var(--border-input);
    background: var(--surface-sunken);
    color: var(--text-primary);
    font: inherit; font-size: 12px;
  }
  .mint-form input:focus, .mint-form select:focus { outline: none; border-color: var(--border-focus); }

  .project-chips { display: flex; flex-wrap: wrap; gap: 4px; }
  .chip {
    appearance: none;
    padding: 4px 8px;
    border-radius: var(--radius-pill);
    border: 0.5px solid var(--border);
    background: var(--surface-base);
    color: var(--text-secondary);
    font: inherit; font-size: 11px;
    cursor: pointer;
  }
  .chip.on { background: var(--accent-subtle); border-color: var(--accent-border); color: var(--accent-text); }
  .chip:hover { background: var(--bg-hover); }
  .empty { font-size: 11px; color: var(--text-tertiary); }

  .actions { display: flex; align-items: center; gap: var(--space-3); margin-top: var(--space-1, 4px); }
  .primary {
    appearance: none;
    background: var(--accent-subtle);
    border: 0.5px solid var(--accent-border);
    color: var(--accent-text);
    border-radius: var(--radius-sm);
    padding: var(--space-2) var(--space-4);
    font: inherit; font-size: 12px;
    cursor: pointer;
  }
  .primary:hover:not(:disabled) { background: var(--accent); color: var(--surface-base); }
  .primary:disabled { opacity: 0.5; cursor: not-allowed; }

  .reveal-card {
    padding: var(--space-3) var(--space-4);
    background: var(--warning-subtle);
    border: 0.5px solid var(--warning);
    border-radius: var(--radius-md);
    display: flex; flex-direction: column; gap: var(--space-2);
  }
  .reveal-header { display: flex; align-items: center; gap: var(--space-3); flex-wrap: wrap; }
  .reveal-badge {
    font-size: 10px; letter-spacing: 0.12em; text-transform: uppercase;
    padding: 2px var(--space-2);
    border-radius: var(--radius-pill);
    background: var(--warning); color: var(--surface-base); font-weight: 600;
  }
  .reveal-name { font-size: 12px; color: var(--text-primary); font-weight: 500; }
  .reveal-meta { font-size: 11px; color: var(--text-tertiary); }
  .reveal-token {
    font-family: var(--type-mono); font-size: 12px;
    padding: var(--space-2) var(--space-3);
    background: var(--surface-base);
    border-radius: var(--radius-sm);
    word-break: break-all;
    color: var(--text-primary);
  }
  .reveal-actions { display: flex; gap: var(--space-3); align-items: center; flex-wrap: wrap; }
  .reveal-actions button {
    appearance: none;
    background: var(--glass-fill);
    border: 0.5px solid var(--border);
    border-radius: var(--radius-sm);
    color: var(--text-primary);
    padding: 4px var(--space-3);
    font: inherit; font-size: 11px;
    cursor: pointer;
  }
  .reveal-actions button:hover { background: var(--bg-hover); }
  .reveal-hint { font-size: 11px; color: var(--text-tertiary); }

  .token-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: var(--space-1, 4px); }
  .token-row {
    display: flex; justify-content: space-between; align-items: center; gap: var(--space-3);
    padding: var(--space-2) var(--space-3);
    background: var(--surface-base);
    border: 0.5px solid var(--border);
    border-radius: var(--radius-sm);
  }
  .token-row.revoked { opacity: 0.6; }
  .token-main { flex: 1; display: flex; flex-direction: column; gap: 4px; min-width: 0; }
  .token-prefix code { font-family: var(--type-mono); font-size: 11px; color: var(--text-primary); }
  .token-label { font-size: 12px; color: var(--text-primary); font-weight: 500; }
  .token-tags { display: flex; gap: 4px; flex-wrap: wrap; }
  .tag {
    font-size: 10px;
    padding: 2px 6px;
    border-radius: var(--radius-pill);
    background: var(--surface-sunken);
    color: var(--text-tertiary);
    text-transform: uppercase; letter-spacing: 0.04em;
  }
  .tag.scope { color: var(--accent-text); background: var(--accent-subtle); }
  .token-meta { font-size: 10px; color: var(--text-tertiary); }
  .token-actions { display: flex; gap: var(--space-3); align-items: center; }
  .status {
    font-size: 10px; padding: 2px 6px; border-radius: var(--radius-pill);
    text-transform: uppercase; letter-spacing: 0.06em;
  }
  .status-active { background: var(--success-subtle, #1f3); color: var(--success-text, #062); }
  .status-revoked { background: var(--surface-sunken); color: var(--text-tertiary); }
  .status-expired { background: var(--warning-subtle); color: var(--warning); }
  .link, .link-muted {
    appearance: none; background: transparent; border: 0;
    font: inherit; font-size: 11px; cursor: pointer; padding: 0;
  }
  .link { color: var(--accent-text); }
  .link:hover { text-decoration: underline; }
  .link-muted { color: var(--text-tertiary); }
  .link-muted.destructive { color: var(--error, #d05); }
  .link-muted:hover { color: var(--text-secondary); }
</style>
